import { HttpClient } from "./HttpClient";
import { getLiteAgentApiUrl } from "./UrlBuilder";
import { IAgencyList, IAgency } from "models";

import { HALManager, UserManager, UserRoleManager } from "core";
import { HttpOperationError } from "shared/utils/exceptions";
import {
  parseToUrlQuery,
  ObjectPath,
  JsonPatchOrderGeneratorWrapper,
} from "shared/utils/http";
import { ISortArrayConfig, sortArray } from "shared/utils/array";

export class AgencyService {
  private httpClient: HttpClient = new HttpClient();

  public async createAgency(agency: IAgency) {
    const urlRequest = getLiteAgentApiUrl(HALManager.postAgencyUrl());

    const requestParams = { body: JSON.stringify(agency) };

    try {
      await this.httpClient.post(urlRequest, requestParams);
    } catch (error) {
      throw new HttpOperationError(
        urlRequest,
        "Creating new agency was failed"
      );
    }
  }

  public async addAgencyForUser({ refNumber }: IAgency) {
    const urlRequest = getLiteAgentApiUrl(
      HALManager.addAgencyUrl(UserManager.artistRef ?? "")
    );

    const requestParams = { body: JSON.stringify({ refNumber }) };

    try {
      await this.httpClient.post(urlRequest, requestParams);
    } catch (error) {
      throw new HttpOperationError(
        urlRequest,
        "Adding agent for user was failed"
      );
    }
  }

  public async searchAgencies(wildcard: string = "") {
    const performerType = UserRoleManager.getUserType();

    const urlRequest = getLiteAgentApiUrl(
      HALManager.searchAgencyUrl(),
      parseToUrlQuery({
        performerType,
        name: wildcard,
        agentsorschools: "agents",
      })
    );

    const searchPromise = async () => {
      try {
        const { object: agencyList } =
          await this.httpClient.get<IAgency[]>(urlRequest);

        return agencyList;
      } catch (error) {
        throw new HttpOperationError(
          urlRequest,
          "Searching agency was failed",
          { wildcard }
        );
      }
    };

    const [agencies = [], { agents: userAgents = [] }] = await Promise.all([
      searchPromise(),
      this.getAgencies(),
    ]);

    const filterFunction = (agency: IAgency) =>
      !userAgents.some((userAgent) => userAgent.refNumber === agency.refNumber);

    const orderingSettings: ISortArrayConfig<IAgency>[] = [
      { expression: (agency) => agency.name, order: "asc" },
    ];

    const filteredAgencies = agencies.filter((agency) =>
      filterFunction(agency)
    );
    const orderedAgencies = sortArray(filteredAgencies, orderingSettings);

    return orderedAgencies;
  }

  public async getAgencies() {
    const urlRequest = getLiteAgentApiUrl(
      HALManager.addAgencyUrl(UserManager.artistRef ?? "")
    );

    try {
      const { object: list } =
        await this.httpClient.get<IAgencyList>(urlRequest);

      return list;
    } catch (error) {
      throw new HttpOperationError(
        urlRequest,
        "Getting agency for user was failed"
      );
    }
  }

  public async deleteAgency(refNumber: number) {
    const urlRequest = getLiteAgentApiUrl(
      HALManager.deleteAgencyUrl(
        UserManager.artistRef ?? "",
        refNumber.toString()
      )
    );

    try {
      await this.httpClient.delete(urlRequest);
    } catch (error) {
      throw new HttpOperationError(urlRequest, "Delete agency was failed", {
        refNumber,
      });
    }
  }

  public async updateLookingForRepresentation(
    isLookingForRepresentation: boolean
  ) {
    const artistRef = UserManager.artistRef ?? "";
    const urlRequest = getLiteAgentApiUrl(
      HALManager.postLookingForRepUrl(artistRef)
    );
    const requestParams = {
      body: JSON.stringify({ IsLookingForRep: isLookingForRepresentation }),
    };

    try {
      await this.httpClient.post(urlRequest, requestParams);
    } catch (error) {
      throw new HttpOperationError(
        urlRequest,
        "updateLookingForRepresentation failed",
        {
          artistRef,
        }
      );
    }
  }

  public async updateList(
    originalAgencySequence: IAgency[],
    newAgencySequence: IAgency[]
  ) {
    newAgencySequence.forEach((agency, index) => (agency.position = index + 1));

    const patchSetting = [
      {
        equals: (left: IAgency, right: IAgency) =>
          left.refNumber === right.refNumber,
        path: ObjectPath.from([]),
      },
    ];

    const patchDiff = new JsonPatchOrderGeneratorWrapper().generate(
      originalAgencySequence,
      newAgencySequence,
      patchSetting
    );

    const urlRequest = getLiteAgentApiUrl(
      HALManager.patchAgencyListUrl(UserManager.artistRef ?? "")
    );

    const requestParams = { body: JSON.stringify(patchDiff) };

    try {
      const { object: agencyList } = await this.httpClient.patch<IAgencyList>(
        urlRequest,
        requestParams
      );

      return agencyList;
    } catch (error) {
      throw new HttpOperationError(
        urlRequest,
        "Updating agent sequence was failed"
      );
    }
  }
}

export default new AgencyService();
